import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PASSWORD_RESET_URL } from "@/lib/publicAppUrl";

const emailSchema = z.string().trim().email("Email inválido").toLowerCase();

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setLoading(true);

    const redirectTo = PASSWORD_RESET_URL;
    console.info("[ForgotPassword] recovery request", {
      email: validation.data,
      redirectTo,
    });

    const { error } = await supabase.auth.resetPasswordForEmail(validation.data, {
      redirectTo,
    });

    console.info("[ForgotPassword] recovery response", {
      ok: !error,
      error: error?.message ?? null,
      status: error?.status ?? null,
    });

    setLoading(false);

    if (error) {
      if (error.message?.includes("rate") || error.status === 429) {
        toast.error("Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.");
      } else {
        toast.error("Erro ao enviar email. Tente novamente.");
      }
      return;
    }

    setSent(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-primary/5 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto bg-primary/10 p-3 rounded-full w-fit">
            {sent ? (
              <Mail className="h-8 w-8 text-primary" />
            ) : (
              <DollarSign className="h-8 w-8 text-primary" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {sent ? "Email enviado" : "Redefinir senha"}
          </CardTitle>
          <CardDescription>
            {sent
              ? "Se existir uma conta com este e-mail, enviaremos um link para redefinir sua senha."
              : "Digite seu e-mail para receber o link de recuperação"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                Verifique sua caixa de entrada e a pasta de spam.
              </p>
              <Link to="/auth">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar ao login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Enviando..." : "Enviar link de recuperação"}
              </Button>
              <div className="text-center">
                <Link
                  to="/auth"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <ArrowLeft className="inline mr-1 h-3 w-3" />
                  Voltar ao login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
